/**
 * The filter panel's `Język` tab: which language the library's names are shown in, and the one
 * sentence that says the search does not depend on it.
 *
 * A tab of its own rather than a footer under the facets — the owner's explicit call. It is NOT a
 * facet: it narrows nothing, so it never counts toward a tab's badge, the active-filter dot or what
 * `Wyczyść` clears, and it writes the same account-wide preference `/konto`'s Aplikacja tab does
 * (`shared/i18n/contentLanguage`), which is why it owns that binding instead of taking it as props.
 * Picking the language the app is already in clears the override rather than pinning it; the store
 * decides that, for both controls.
 *
 * Its two hosts draw a tab's heading in two different skins — the drawer's panel (on a host that
 * opts in: the survey editor's picker) and the library screen's popover — so the heading's class is
 * the host's, and everything else is this file's.
 *
 * The words are the phone's own (`exerciseLibrary.contentLang.*`, the verbatim copy), bar the note:
 * the search covers both languages here as it does on the phone, and the owner asked for that to be
 * said once, in one sentence, under the choice.
 */

import { useTranslation } from 'react-i18next';

import {
  CONTENT_LANGUAGES,
  setContentLanguage,
  useContentLanguage,
} from '~/shared/i18n/contentLanguage';
import { SegmentedChoice } from '~/shared/ui/SegmentedChoice';

import styles from './ExerciseLanguageChoice.module.css';

export interface ExerciseLanguageChoiceProps {
  /** The host's heading skin — the drawer's and the library screen's differ. */
  readonly titleClassName: string;
}

export function ExerciseLanguageChoice({ titleClassName }: ExerciseLanguageChoiceProps) {
  const { t } = useTranslation();
  const contentLanguage = useContentLanguage();

  return (
    <div className={styles.language}>
      <span className={titleClassName}>{t('exerciseLibrary.contentLang.title')}</span>
      <SegmentedChoice
        legend={t('exerciseLibrary.contentLang.a11y')}
        options={CONTENT_LANGUAGES.map((value) => ({
          value,
          label: t(`exerciseLibrary.contentLang.${value}`),
        }))}
        value={contentLanguage}
        onChange={setContentLanguage}
        /* `ink`, not the `raised` groove: both panels are `--bg-card`, and a card-coloured pill
         * lifted on a card is the chosen option drawn in the ground's own colour (1.05:1). */
        tone="ink"
      />
      <p className={styles.note}>{t('web:exerciseLibrary.language.searchNote')}</p>
    </div>
  );
}
